// Перевод интерфейса без правки компонентов.
// Исходный текст (русский) остаётся в разметке и коде; после отрисовки переводчик проходит по DOM
// и заменяет текстовые узлы и атрибуты (placeholder, title, aria-label) по словарю и шаблонам.
// MutationObserver ловит всё, что React дорисовывает позже. Оригинал хранится в WeakMap,
// поэтому возврат на русский восстанавливает текст, а повторный проход не переводит дважды.
// React меняет текстовые узлы только при смене своих данных — переведённый узел он не трогает,
// а новый русский текст снова ловит наблюдатель.
//
// Без document/localStorage (тесты в Node) работает только translateText().
//
// Словарь загружается отдельными файлами, только если язык не русский (loadDict): общие шаблоны и даты (dict-rules)
// и таблица строк одного языка (virtual:i18n-uz / virtual:i18n-en) — русскоязычным посетителям он не нужен,
// узбекскому не нужны английские строки. Пока словарь не загружен, translateText() возвращает текст как есть.
// В тестах полный словарь (оба языка) подключается сразу (registerDict в src/test-setup.ts).
import type { I18nPattern } from './dict-rules';

type DictModule = typeof import('./dict');
type RulesModule = typeof import('./dict-rules');

export type Lang = 'ru' | 'uz' | 'en';
export const LANGS: Lang[] = ['ru', 'uz', 'en'];
const LOCALES: Record<Lang, string> = { ru: 'ru-RU', uz: 'uz-Latn-UZ', en: 'en-GB' };
export const LANG_NAMES: Record<Lang, string> = { ru: 'Русский', uz: 'Oʻzbekcha', en: 'English' };
// Для этих языков интерфейса по умолчанию русский
const CIS = ['ru', 'be', 'uk', 'kk', 'ky', 'tg', 'tk', 'hy', 'az', 'ka', 'mn'];
const STORE_KEY = 'lang';
const ATTRS = ['placeholder', 'title', 'aria-label'];

const CYR = /[А-Яа-яЁё]/;
const NBSP = /[  ]/g;
let patterns: I18nPattern[] = [];
let dict: DictModule['dict'] | null = null;
let dates: DictModule['dates'];
let tokens: DictModule['tokens'];
const hasDom = typeof document !== 'undefined' && typeof window !== 'undefined';
const isLang = (v: unknown): v is Lang => typeof v === 'string' && (LANGS as string[]).includes(v);

let current: Lang | null = null;

// ================= Язык =================

function detect(): Lang {
  if (typeof location !== 'undefined') {
    const q = new URLSearchParams(location.search).get('lang');
    if (isLang(q)) { save(q); return q; }
  }
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      let v: unknown = raw;
      try { v = JSON.parse(raw); } catch { /* старый формат — строка */ }
      if (isLang(v)) return v;
    }
  } catch { /* нет хранилища */ }
  const nav: string[] = typeof navigator !== 'undefined'
    ? (navigator.languages && navigator.languages.length ? [...navigator.languages] : [navigator.language])
    : [];
  for (const l of nav) {
    const code = String(l || '').toLowerCase().slice(0, 2);
    if (code === 'uz') return 'uz';
    if (CIS.includes(code)) return 'ru';
    if (code === 'en') return 'en';
  }
  return nav.filter(Boolean).length ? 'en' : 'ru';
}

function save(lang: Lang): void {
  // Формат как у store (JSON)
  try { localStorage.setItem(STORE_KEY, JSON.stringify(lang)); } catch { /* нет хранилища */ }
}

export function getLang(): Lang {
  if (!current) current = detect();
  return current;
}

export function locale(lang: Lang = getLang()): string {
  return LOCALES[lang] || LOCALES.ru;
}

// ================= Перевод строки =================

type Target = 'uz' | 'en';
const cache: Record<Target, Map<string, string>> = { uz: new Map(), en: new Map() };
const CACHE_MAX = 5000;

// Сначала длинные формы, чтобы «ноября» не съелось как «нояб»
const alt = (list: string[]) => [...list].sort((a, b) => b.length - a.length).join('|');
const NB = '[\\s\\u00a0\\u202f]';
const NOT_L = '(?![А-Яа-яЁёA-Za-z])';
const MONTH_FORMS = new Map<string, { i: number; short: boolean }>();
const WD_FORMS = new Map<string, { i: number; short: boolean }>();
let RE_DAY_MONTH = /$^/g;
let RE_MONTH_YEAR = /$^/g;
let RE_MONTH_ONLY = /$^/;
let RE_WEEKDAY = /$^/;

/** Подключить полный словарь (тесты) */
export function registerDict(mod: DictModule): void {
  if (dict) return;
  registerRules(mod);
  dict = mod.dict;
  loaded.add('uz');
  loaded.add('en');
}

let rulesReady = false;
/** Шаблоны, даты и формы слов — общие для языков */
function registerRules(mod: RulesModule): void {
  if (rulesReady) return;
  rulesReady = true;
  dates = mod.dates;
  tokens = mod.tokens;
  // Пробел в шаблоне совпадает и с неразрывным (Intl.NumberFormat('ru-RU') и местами в коде)
  patterns = mod.patterns.map((p) => ({ ...p, re: new RegExp(p.re.source.replace(/ /g, '[   ]'), p.re.flags) }));
  const R = dates.ru;
  R.monthsGen.forEach((m, i) => MONTH_FORMS.set(m, { i, short: false }));
  R.monthsNom.forEach((m, i) => MONTH_FORMS.set(m, { i, short: false }));
  R.monthsShort.forEach((m, i) => { if (!MONTH_FORMS.has(m)) MONTH_FORMS.set(m, { i, short: true }); });
  const MONTH_RE = alt([...MONTH_FORMS.keys()]);
  // «15 ноября», «15 нояб.», «15 нояб», «15 ноября 2026 г.»
  RE_DAY_MONTH = new RegExp(`(?<![\\dА-Яа-яЁё])(\\d{1,2})${NB}(${MONTH_RE})(\\.?)${NOT_L}(?:${NB}(\\d{4})(?:${NB}г\\.)?)?`, 'gi');
  // «ноябрь 2026 г.», «Ноябрь 2026», «нояб. 27»
  RE_MONTH_YEAR = new RegExp(`(?<![А-Яа-яЁё])(${MONTH_RE})(\\.?)${NB}(\\d{2,4})(?:${NB}г\\.)?(?![\\d])`, 'gi');
  // Месяц или день недели как вся строка: «нояб.», «вс», «воскресенье»
  RE_MONTH_ONLY = new RegExp(`^(${MONTH_RE})\\.?$`, 'i');
  R.weekdays.forEach((w, i) => WD_FORMS.set(w, { i, short: false }));
  R.weekdaysShort.forEach((w, i) => WD_FORMS.set(w, { i, short: true }));
  RE_WEEKDAY = new RegExp(`^(${alt([...WD_FORMS.keys()])})(?=,|$)`, 'i');
}

const loaded = new Set<Target>();
const loading: Partial<Record<Target, Promise<void>>> = {};
/** Загрузить словарь языка отдельными файлами (один раз на язык); по умолчанию — текущего */
export function loadDict(lang: Lang = getLang()): Promise<void> {
  if (lang === 'ru' || loaded.has(lang)) return Promise.resolve();
  loading[lang] ??= Promise.all([import('./dict-rules'), lang === 'uz' ? import('virtual:i18n-uz') : import('virtual:i18n-en')])
    .then(([rules, table]) => {
      registerRules(rules);
      dict = { uz: {}, en: {}, ...dict, [lang]: rules.expandCase(table.default) };
      loaded.add(lang);
      reverse = null;
      cache[lang].clear();
    });
  return loading[lang]!;
}

/** Словарь уже подключён */
export const dictReady = (): boolean => dict !== null;

const capFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const isUpper = (s: string) => s.charAt(0) !== s.charAt(0).toLowerCase();

// keepCase — заглавная, если заглавная в оригинале («Ноябрь 2026» в шапке календаря);
// в «15-noyabr» узбекский месяц всегда со строчной
function monthName(form: string, lang: Target, keepCase = false): string | null {
  const m = MONTH_FORMS.get(form.toLowerCase());
  if (!m) return null;
  const D = dates[lang];
  let out = m.short ? D.monthsShort[m.i] : D.months[m.i];
  if (keepCase && isUpper(form)) out = capFirst(out);
  return out;
}

// Даты, «сум», «млн», «тыс.» внутри строки
function translateTokens(s: string, lang: Target): string {
  const D = dates[lang];
  const TK = tokens[lang];
  let out = s;
  out = out.replace(RE_DAY_MONTH, (all, d: string, form: string, _dot: string, y?: string) => {
    const name = monthName(form, lang);
    if (!name) return all;
    const yr = y ? ` ${y}` : '';
    return lang === 'uz' ? `${d}-${name}${yr}` : `${d} ${name}${yr}`;
  });
  out = out.replace(RE_MONTH_YEAR, (all, form: string, _dot: string, y: string) => {
    const name = monthName(form, lang, true);
    return name ? `${name} ${y}` : all;
  });
  out = out.replace(RE_WEEKDAY, (all, w: string) => {
    const f = WD_FORMS.get(w.toLowerCase());
    if (!f) return all;
    const name = f.short ? D.weekdaysShort[f.i] : D.weekdays[f.i];
    return isUpper(w) || lang === 'en' ? capFirst(name) : name;
  });
  const mo = out.match(RE_MONTH_ONLY);
  if (mo) {
    const name = monthName(mo[1], lang, true);
    if (name) out = name;
  }
  out = out
    .replace(new RegExp(`(?<=(?:\\d|млн|млрд|тыс\\.)${NB})сум${NOT_L}`, 'g'), TK['сум'])
    .replace(new RegExp(`(\\d)(${NB})млрд${NOT_L}`, 'g'), `$1$2${TK['млрд']}`)
    .replace(new RegExp(`(\\d)(${NB})млн${NOT_L}`, 'g'), `$1$2${TK['млн']}`)
    .replace(new RegExp(`(\\d)(${NB})тыс\\.`, 'g'), `$1$2${TK['тыс.']}`)
    // tinyPrice: «29,7к», «1,2м»
    .replace(/^([\d,.]+)к$/, `$1${TK['к']}`)
    .replace(/^([\d,.]+)м$/, `$1${TK['м']}`);
  return out;
}

function applyPattern(p: I18nPattern, m: RegExpMatchArray, lang: Target, depth: number): string {
  const groups = m.slice(1);
  const rawIdx = p.raw === true ? null : new Set(p.raw || []);
  const tr = groups.map((g, i) => {
    if (g == null) return g;
    if (p.raw === true || rawIdx!.has(i + 1)) return g;
    return CYR.test(g) ? translateCore(g, lang, depth + 1) : g;
  });
  const tpl = p[lang];
  if (typeof tpl === 'function') return tpl(...(tr as string[]));
  return tpl.replace(/\$(\d)/g, (_, n: string) => tr[Number(n) - 1] ?? '');
}

// Названия валют из Intl.DisplayNames(['ru']): «турецкая лира» → «Turkish lira»
const CURRENCY_CODES = ['USD', 'EUR', 'RUB', 'UZS', 'KZT', 'KGS', 'TJS', 'TRY', 'AED', 'GEL', 'AZN', 'AMD', 'BYN', 'UAH', 'THB',
  'MYR', 'MVR', 'EGP', 'GBP', 'CNY', 'INR', 'JPY', 'KRW', 'VND', 'IDR', 'SAR', 'QAR', 'OMR', 'KWD', 'BHD', 'JOD', 'ILS',
  'CHF', 'CZK', 'PLN', 'HUF', 'LKR', 'SGD', 'HKD', 'MAD', 'TND', 'PHP', 'NPR', 'PKR', 'CAD', 'AUD', 'SEK', 'NOK', 'DKK', 'RSD', 'BGN', 'RON'];
let currencyNames: Record<Target, Map<string, string>> | null = null;
function currencyName(s: string, lang: Target): string | null {
  if (!currencyNames) {
    currencyNames = { uz: new Map(), en: new Map() };
    try {
      const ru = new Intl.DisplayNames(['ru'], { type: 'currency' });
      const other = { uz: new Intl.DisplayNames(['uz-Latn', 'uz'], { type: 'currency' }), en: new Intl.DisplayNames(['en'], { type: 'currency' }) };
      for (const code of CURRENCY_CODES) {
        const name = ru.of(code);
        if (!name || name === code) continue;
        for (const l of ['uz', 'en'] as const) {
          const v = other[l].of(code);
          if (v && v !== code && !CYR.test(v)) currencyNames[l].set(name.toLowerCase(), v);
        }
      }
    } catch { /* нет Intl.DisplayNames */ }
  }
  const v = currencyNames[lang].get(s.toLowerCase());
  return v ? (isUpper(s) ? capFirst(v) : v) : null;
}

function matchExactOrPattern(s: string, lang: Target, depth: number, loose = false): string | null {
  if (!dict) return null;
  const d = dict[lang];
  if (Object.prototype.hasOwnProperty.call(d, s)) return d[s];
  const plain = s.replace(NBSP, ' ');
  if (plain !== s && Object.prototype.hasOwnProperty.call(d, plain)) return d[plain];
  const cur = s.length < 40 ? currencyName(s, lang) : null;
  if (cur) return cur;
  for (const p of patterns) {
    if (Boolean(p.loose) !== loose) continue;
    const m = s.match(p.re);
    if (m) return applyPattern(p, m, lang, depth);
  }
  return null;
}

// Разбить по разделителю и перевести части; годится, если поменялась хоть одна часть
// sep — регулярное выражение с одной скобкой: разделители сохраняются как есть
function splitTranslate(s: string, sep: RegExp, lang: Target, depth: number): string | null {
  const parts = s.split(sep);
  if (parts.length < 3) return null;
  let changed = false;
  const out = parts.map((part, i) => {
    if (i % 2) return part;
    const t = translateCore(part, lang, depth + 1);
    if (t !== part) changed = true;
    return t;
  });
  return changed ? out.join('') : null;
}
const SEP_DOT = /([  ]·[  ])/;
const SEP_COMMA = /(,[  ])/;
const SEP_COLON = /(:[  ])/;
const SEP_DASH = /([   ]—[   ])/;

const LEAD = /^[\s·,:;—–\-]+/;
const TRAIL = /[\s·,:;—–\-.!?]+$/;

function translateCore(s: string, lang: Target, depth = 0): string {
  if (!s || !CYR.test(s) || depth > 6) return s;
  const c = cache[lang];
  const hit = c.get(s);
  if (hit !== undefined) return hit;
  let out = matchExactOrPattern(s, lang, depth);

  // Та же фраза с другим регистром первой буквы (кроме коротких: «вс» ≠ «Вс»)
  if (out == null && s.length > 3 && /^[А-ЯЁа-яё]/.test(s)) {
    const flipped = (isUpper(s) ? s.charAt(0).toLowerCase() : s.charAt(0).toUpperCase()) + s.slice(1);
    const t = matchExactOrPattern(flipped, lang, depth);
    if (t != null) out = isUpper(s) ? capFirst(t) : t.charAt(0).toLowerCase() + t.slice(1);
  }

  // Даты и «сум» внутри строки: сначала заменяем их, остаток переводим дальше
  if (out == null) {
    const t = translateTokens(s, lang);
    if (t !== s) out = CYR.test(t) ? translateCore(t, lang, depth + 1) : t;
  }

  // Знаки по краям: «· в одну сторону», «Ночей в поездке:», «, чтобы получать уведомления.»
  if (out == null) {
    const lead = (s.match(LEAD) || [''])[0];
    const rest = s.slice(lead.length);
    const trail = (rest.match(TRAIL) || [''])[0];
    const core = rest.slice(0, rest.length - trail.length);
    if (core && (lead || trail)) {
      const t = translateCore(core, lang, depth + 1);
      if (t !== core) out = lead + t + trail;
    }
  }

  // Составные строки: «прямой · вылет 12:30 · в пути 3 ч», «вс, 15 нояб., прямой», «Стамбул: 15 нояб. — 18 нояб.»
  if (out == null) out = splitTranslate(s, SEP_DOT, lang, depth);
  if (out == null) out = splitTranslate(s, SEP_COMMA, lang, depth);
  if (out == null) out = splitTranslate(s, SEP_COLON, lang, depth);
  // Диапазон дат «вт, 3 нояб. — пн, 23 нояб.»: день недели второй даты
  if (out == null) out = splitTranslate(s, SEP_DASH, lang, depth);
  if (out == null) {
    for (const p of patterns) {
      if (!p.loose) continue;
      const m = s.match(p.re);
      if (m) { out = applyPattern(p, m, lang, depth); break; }
    }
  }
  if (out == null) out = translateTokens(s, lang);

  if (c.size > CACHE_MAX) c.clear();
  c.set(s, out);
  return out;
}

/**
 * Перевод одной строки с русского. Чистая функция (без DOM).
 * Пробелы по краям сохраняются; внутренние переводы строк схлопываются только для поиска.
 */
export function translateText(str: string, lang?: Lang): string;
export function translateText(str: string | null | undefined, lang?: Lang): string | null | undefined;
export function translateText(str: string | null | undefined, lang: Lang = getLang()): string | null | undefined {
  if (lang === 'ru' || !isLang(lang) || str == null || !dict) return str;
  const s = String(str);
  if (!CYR.test(s)) return s;
  const m = s.match(/^(\s*)([\s\S]*?)(\s*)$/)!;
  const core = m[2].replace(/[ \t\r\n]+/g, ' ');
  const out = translateCore(core, lang);
  if (out === core && core === m[2]) return s;
  return m[1] + out + m[3];
}

// Перевод для кода: t('Найти') → 'Qidirish' (нужно редко: DOM переводится сам)
export function t(ruText: string): string {
  return translateText(ruText, getLang());
}

// Обратный словарь: текст, который код прочитал из уже переведённой разметки и вставил заново,
// при смене языка возвращаем к русскому оригиналу.
let reverse: Map<string, string> | null = null;
function toRussian(text: string): string | null {
  if (!dict) return null;
  if (!reverse) {
    reverse = new Map();
    for (const lang of ['uz', 'en'] as const) {
      for (const [ru, v] of Object.entries(dict![lang])) {
        if (v.length >= 3 && /[A-Za-zʻʼ]/.test(v) && !reverse.has(v)) reverse.set(v, ru);
      }
    }
  }
  const m = text.match(/^(\s*)([\s\S]*?)(\s*)$/)!;
  const ru = reverse.get(m[2].replace(/[ \t\r\n]+/g, ' '));
  return ru ? m[1] + ru + m[3] : null;
}

// ================= DOM =================

// Внутрь не заходим совсем (документация RapiDoc, помеченное data-no-i18n, редактируемое)
const SKIP_ALL = 'rapi-doc, [data-no-i18n], [contenteditable=""], [contenteditable="true"]';
// Текст внутри не трогаем (код, стили, введённое в textarea), но атрибуты самого элемента — переводим
const SKIP_TEXT = 'script, style, textarea, noscript, code, pre, template';
interface Rec { src: string; out?: string }
const textRec = new WeakMap<Text, Rec>();
const attrRec = new WeakMap<Element, Record<string, Rec>>();
let observer: MutationObserver | null = null;
let pending = new Set<Node>();
let scheduled = false;
let touched = false; // переводили ли хоть раз (пока нет и язык ru — наблюдателю нечего делать)

const inside = (el: Element | null, sel: string) => Boolean(el && el.closest && el.closest(sel));

function resolve(rec: Rec | undefined, cur: string): Rec | null {
  // Узел новый или код поменял его текст — запоминаем новый оригинал
  if (rec && cur === rec.out) return rec;
  if (CYR.test(cur)) return { src: cur };
  const ru = toRussian(cur);
  return ru ? { src: ru } : null;
}

function processText(node: Text): void {
  const cur = node.data;
  const rec = resolve(textRec.get(node), cur);
  if (!rec) return;
  const out = current === 'ru' ? rec.src : translateText(rec.src, current!);
  rec.out = out;
  textRec.set(node, rec);
  if (out !== cur) node.data = out;
}

function processAttrs(el: Element): void {
  let recs = attrRec.get(el);
  for (const a of ATTRS) {
    const cur = el.getAttribute(a);
    if (cur == null) continue;
    const rec = resolve(recs && recs[a], cur);
    if (!rec) continue;
    const out = current === 'ru' ? rec.src : translateText(rec.src, current!);
    rec.out = out;
    if (!recs) attrRec.set(el, (recs = {}));
    recs[a] = rec;
    if (out !== cur) el.setAttribute(a, out);
  }
}

function walk(root: Node | null): void {
  if (!root) return;
  if (root.nodeType === 3) {
    const el = root.parentElement;
    if (el && !inside(el, SKIP_ALL) && !inside(el, SKIP_TEXT)) processText(root as Text);
    return;
  }
  if (root.nodeType !== 1 && root.nodeType !== 9 && root.nodeType !== 11) return;
  if (root.nodeType === 1) {
    const el = root as Element;
    if (inside(el, SKIP_ALL)) return;
    processAttrs(el);
    if (inside(el, SKIP_TEXT)) return;
  }
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      if (n.nodeType !== 1) return NodeFilter.FILTER_ACCEPT;
      const el = n as Element;
      if (el.matches(SKIP_ALL)) return NodeFilter.FILTER_REJECT;
      if (el.matches(SKIP_TEXT)) { processAttrs(el); return NodeFilter.FILTER_REJECT; }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  for (let n = tw.nextNode(); n; n = tw.nextNode()) {
    if (n.nodeType === 3) processText(n as Text);
    else processAttrs(n as Element);
  }
}

function translateTitle(): void {
  const el = document.querySelector('head > title');
  if (el) walk(el);
}

function flush(): void {
  scheduled = false;
  const nodes = pending;
  pending = new Set();
  if (current === 'ru' && !touched) return;
  for (const n of nodes) {
    if (!n.isConnected) continue;
    walk(n);
  }
}

function schedule(): void {
  if (scheduled) return;
  scheduled = true;
  // rAF срабатывает до отрисовки кадра — русский текст не мелькает; в фоновой вкладке rAF не идёт
  if (typeof requestAnimationFrame === 'function' && !document.hidden) requestAnimationFrame(flush);
  else setTimeout(flush, 30);
}

function onMutations(list: MutationRecord[]): void {
  for (const m of list) {
    if (m.type === 'childList') m.addedNodes.forEach((n) => pending.add(n));
    else pending.add(m.target);
  }
  if (pending.size) schedule();
}

/** Перевод готового HTML-документа: окно печати (PDF) открывается отдельно, наблюдатель страницы его не видит.
 *  Тексты и атрибуты — как на странице; код, стили и помеченное data-no-i18n не трогаем. */
export function translateHtml(html: string, lang: Lang = getLang()): string {
  if (lang === 'ru' || !dict || typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.documentElement.lang = lang;
  const tw = doc.createTreeWalker(doc.documentElement, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  for (let n = tw.nextNode(); n; n = tw.nextNode()) {
    if (n.nodeType === 3) {
      const el = n.parentElement;
      if (!el || inside(el, SKIP_ALL) || inside(el, SKIP_TEXT)) continue;
      const t = n as Text;
      const out = translateText(t.data, lang);
      if (out !== t.data) t.data = out;
    } else {
      const el = n as Element;
      if (inside(el, SKIP_ALL)) continue;
      for (const a of ATTRS) {
        const v = el.getAttribute(a);
        if (v && CYR.test(v)) el.setAttribute(a, translateText(v, lang));
      }
    }
  }
  return `<!doctype html>${doc.documentElement.outerHTML}`;
}

/** Перевести всю страницу заново (после смены языка). */
export function translatePage(): void {
  if (!hasDom) return;
  if (current !== 'ru') touched = true;
  document.documentElement.lang = current!;
  translateTitle();
  walk(document.body);
}

let dialogsWrapped = false;

/** Системные окна alert/confirm/prompt — на языке интерфейса: их текст не попадает в DOM, наблюдатель его не видит.
 *  Значение по умолчанию в prompt — данные пользователя (имя, сумма), его не переводим. */
function wrapDialogs(): void {
  if (dialogsWrapped) return;
  dialogsWrapped = true;
  const w = window as unknown as Record<'alert' | 'confirm' | 'prompt', (msg?: unknown, def?: string) => unknown>;
  for (const k of ['alert', 'confirm', 'prompt'] as const) {
    const orig = w[k];
    if (typeof orig !== 'function') continue;
    w[k] = (msg?: unknown, def?: string) => (k === 'prompt'
      ? orig.call(window, typeof msg === 'string' ? translateText(msg) : msg, def)
      : orig.call(window, typeof msg === 'string' ? translateText(msg) : msg));
  }
}

/** Запуск: определить язык, перевести страницу и следить за изменениями. */
export function initI18n(): Lang {
  getLang();
  if (!hasDom) return current!;
  wrapDialogs();
  translatePage();
  if (!observer) {
    observer = new MutationObserver(onMutations);
    observer.observe(document.documentElement, {
      subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS,
    });
  }
  return current!;
}

/** Сменить язык: запомнить, перевести страницу, сообщить событием «langchange».
 *  Словарь ещё не загружен — сначала загружаем его, потом переключаем. */
export function setLang(lang: Lang): void {
  if (!isLang(lang)) return;
  if (lang !== 'ru' && !loaded.has(lang) && hasDom) { void loadDict(lang).then(() => setLang(lang), () => {}); return; }
  const changed = lang !== current;
  current = lang;
  save(lang);
  if (!hasDom) return;
  translatePage();
  if (changed) window.dispatchEvent(new CustomEvent('langchange', { detail: { lang, locale: locale(lang) } }));
}
